import * as _omit from 'lodash/omit';
import * as _cloneDeep from 'lodash/cloneDeep';
import * as _isNull from 'lodash/isNull';
import * as _isUndefined from 'lodash/isUndefined';
import * as _range from 'lodash/range';
import * as _clamp from 'lodash/clamp';
import * as _reduce from 'lodash/reduce';
import * as _difference from 'lodash/difference';
import * as _get from 'lodash/get';
import * as _mapValues from 'lodash/mapValues';
import * as _isEqual from 'lodash/isEqual';
import * as _isFunction from 'lodash/isFunction';
import * as _capitalize from 'lodash/capitalize';
import * as _flip from 'lodash/flip';
import * as _negate from 'lodash/negate';
import * as _partial from 'lodash/partial';
import * as _includes from 'lodash/includes';
import {matchPath} from 'react-router-dom';
import {Dropdown} from 'semantic-ui-react';
import moment from 'moment';
import React from 'react';
import PropTypes from 'prop-types';
import * as ResourceApi from 'api/resources';
import Config from 'Config';

/*
    Constants
 */
export const NullUUID = "00000000-0000-0000-0000-000000000000";
export const EmptyFunc = () => (null);
export const TopLevelSectionName = "TopLevelAccordions";

/*
    Object Utils
 */

/**
 * Wrapper for lodash's omit
 * @param obj
 * @param paths
 */
export const omit = (obj, ...paths) => {
    return _omit.default(obj, ...paths);
};

/**
 * Wrapper for lodash's cloneDeep. If there is an error during clone, original object is returned.
 * @param obj
 */
export const clone = (obj) => {
    try {
        return _cloneDeep.default(obj);
    } catch (err) {
        console.error("Failed to clone object. Returning original.", obj);
        return obj;
    }
};

/**
 * Faster clone, when deep clone isn't necessary, or cloning recursive structures
 * @param obj
 * @return {*}
 */
export const shallowClone = (obj) => {
    try {
        return Object.assign({}, obj);
    } catch (err) {
        console.error("Failed to shallow clone object. Returning original.", obj);
        return obj;
    }
};

export const isNull = (obj) => _isNull.default(obj);
export const isUndefined = (obj) => _isUndefined.default(obj);
export const isNullOrUndefined = (obj) => isNull(obj) || isUndefined(obj);
export const isNoU = isNullOrUndefined;
export const isBlank = (str) => isNoU(str) || (typeof(str) === 'string' && str.trim() === "");

// String Utilities

export const removeExtension = (str) => str ? str.slice(0, str.lastIndexOf(".")) : str;
export const zeroPad = (num, len) => String(num).padStart(len, '0');

export const replaceAll = (str, search, replacement) => {
  if ( !str ) { return str; }
  return str.toString().replace(new RegExp(search, 'g'), replacement);
}

const tagsToRemove = ['script', 'meta', 'style'];
const allowedTags = ['b', 'strong', 'a', 'i', 'em', 'u', 's', 'del', 'p', 'br', 'ul', 'li', 'ol'];
const allowedAttributes = ['href'];

function cleanChildren(node) {
  // Collect nodes to remove, and do removal later so iteration skip any elements
  let toRemove = [];
  for (let child of node.childNodes) {
    if (child.nodeType === 1) { // ELEMENT_NODE
      let name = child.nodeName.toLowerCase();
      // If we're explicitly removing a tag OR the element has no visible content
      // except for br tags, they're allowed to be empty
      if (tagsToRemove.indexOf(name) !== -1
        || (child.textContent.trim() === "" && name !== 'br')) {

        // Edge case for span tags
        // Take mark up
        // <p>My <em>fancy</em> sentence.</p>
        // During parsing the spaces around the 'em' tag are replaced with <span>&nbsp;</span>
        // so if we run into on of these spans, we're going to assume it wasn't there originally and replace it with a space character
        if (name === 'span' && child.innerHTML === '&nbsp;') {
          let textNode = document.createTextNode(' ');
          node.replaceChild(textNode, child);
        } else {
          toRemove.push(child);
        }
      } else {
        // Create a new node of same type
        // More precise than trying to remove everything one by one
        let newNodeName = name;
        if (allowedTags.indexOf(name) === -1) {
          // Not an allowed tag, replace with p
          newNodeName = 'p';
        }

        let newNode = document.createElement(newNodeName);
        newNode.innerHTML = child.innerHTML;

        // Copy over all allowed attributes
        for (let attr of child.attributes) {
          if (allowedAttributes.indexOf(attr.name) !== -1) {
            newNode.setAttribute(attr.name, attr.value);
          }
        }

        node.replaceChild(newNode, child);
        cleanChildren(newNode);
      }
    }
  }

  for (let child of toRemove) {
    node.removeChild(child);
  }
}

function isWordDoc(doc) {
  const html = doc.querySelector('html');
  return html.getAttribute('xmlns:w') && html.getAttribute('xmlns:w').includes('schemas-microsoft-com');
}

function isGoogleDoc(doc) {
  const b = doc.querySelector('body > b');
  return b && b.id.includes('docs-internal-guid');
}

// XSS-safe HTML entity decoder, cf. https://stackoverflow.com/a/34064434
export const textToCleanHTMLStr = str => {
    // console.log(str);
    // console.log(new DOMParser().parseFromString(str, 'text/html'));

    const doc = new DOMParser().parseFromString(str, 'text/html');
    const body = doc.querySelector('body');

    // console.dir(body);
    if (isWordDoc(doc) || isGoogleDoc(doc)) {
      // Destroy all formatting until we can get special parsers in for these formats
      return body.textContent;
    } else {
      // Our own parsing, implemented mainly for copying text from other websites
      cleanChildren(body);
      return body.innerHTML;
    }
};

// textContent and innerText cannot reliably be used in calculating the true text length, so here we
// do a few things to work around that:
//   - normalize linebreaks (<br> and/or a line being wrapped in a div) as space characters
//   - "decode" &nbsp;
//   - strip out any tags that can be added through our contenteditable formatting buttons

const tagsToStrip = ['div', 'strike', 'ul', 'ol', 'li', 'span', 'b', 'i', 'u', 'sub', 'sup'];
export const parseContentEditable = html => {
  let txt = html;
  for (let tag of tagsToStrip) {
    txt = txt.replace(new RegExp(`<\/?${tag}\/?>`, 'g'), '')
  }

  return txt.replace(/&nbsp;/g, ' ')
            .replace(/<br\/?>/g, ' ')
            .replace(/<\/?(a(\shref=".*")?)\/?>/g, ''); // special regex for link with href to no over complicate shared regex
};

/**
 * Use to wrap strings in quotes for logging and display to differentiate actual string values from other types
 * @param obj
 */
export const wrapIfStr = (obj) => ((typeof obj) === 'string') ? `"${obj}"` : obj;
export const formatKilobytes = (bytes) => isNumber(bytes) ? `${(bytes / 1000).toFixed(0)}KB` : "";

export const formatDate = (date, pattern) => {
    if (isFalsy(date)) {
        return "";
    }

    // Coerce to instance of Date
    let dateObj = date;
    if (isNumber(date) || typeof(date) === 'string') {
        dateObj = new Date(date);
    }

    if (!(dateObj instanceof Date)) {
        return "";
    }

    if ( typeof(pattern) !== 'undefined' ) {
      return moment( dateObj ).format(pattern);
    }

    return dateObj.toDateString();
};

export const formatLocalDateTime = ({date: {year, month, day}, time: {hour, minute, second}}, formatString) => {
  const date = new Date(year, month - 1, day, hour, minute, second);
  return formatDate(date, formatString);
};

/**
* Converts seconds into mm:ss format
*/
export const secondsToMMSS = (seconds) => {
  let rounded = Math.round(seconds);
  let mins = Math.floor(rounded / 60);
  let secs = rounded % 60;

  return ((mins < 10 ? "0" : "") + mins + ":" + ((secs < 10) ? "0" : "") + secs);
};

/*
    Number Utilities
 */

// isNaN polyfill according to: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/isNaN
export const isNaN = Number.isNaN || ((value) => value !== value);
export const isNumber = (num) => num !== null && num !== undefined && !isNaN(num) && typeof num === "number";

/*
    Boolean Utilities
 */

// Use to force an explicit boolean value out of any value
export const isTruthy = (value) => !!(value);
export const isFalsy = (value) => !(value);

/*
    Date Utilities
 */

export const daysAgo = (numDays) => moment().startOf('day').subtract(7, 'days').valueOf();

/*
    Image Utilities
 */

export const TempImageType = "__temp__";
export const makeDBOriginalImageUrl = (imageId) => `${Config.ORIGINAL_IMAGE_BASE_URL}/${imageId}`;
export const makeDBContentImageUrl = (imageId) => `${Config.CONTENT_IMAGE_BASE_URL}/${imageId}`;
export const makeDBUGCImageUrl = (imageId) => `${Config.UGC_IMAGE_BASE_URL}/${imageId}`;
export const makeDBTempImageUrl = (imageId) => `${Config.TEMP_IMAGE_BASE_URL}/${imageId}`;
export const makeToolTipUrl = (path) => `${Config.TOOLTIP_IMAGE_BASE_URL}/${path}`;
export const makeDBImageUrl = (image) => {
    if (isNoU(image)) {
        return "";
    }

    if (image.imageType === "original") {
        return makeDBOriginalImageUrl(image.id);
    } else if (image.imageType === TempImageType) {
        return makeDBTempImageUrl(image.id);
    } else if (isNoU(image.imageType)) {
        return makeDBUGCImageUrl(image.id); // Is this true?
    } else {
        return makeDBContentImageUrl(image.id);
    }
};

/*
    Pagination Utils
 */

export const pageCount = (total, size) => isNumber(size) && size > 0 ? Math.ceil(total/size) : 0;
// Simple logic util to prepend recent (new) items to a page if it is the first page
// and slices new page to count
export const prependRecentIfFirstPage = (page, count, recent, items) => {
    if (page !== 1) {
        return items;
    }

    // Avoid duplicates, can only be done with ids on the items
    let addItems = [];
    for (let item of recent) {
        if (isFalsy(items.find((i) => item.id && i.id && (item.id === i.id)))) {
            addItems.push(item);
        }
    }

    let newList = addItems.concat(items.slice());

    return newList.slice(0, count);
};

/*
    Lodash aliases
 */

export const range = _range.default;
export const clamp = _clamp.default;
export const reduce = _reduce.default;
export const difference = _difference.default;
export const get = _get.default;
export const mapValues = _mapValues.default;
export const isEqual = _isEqual.default;
export const isFunction = _isFunction.default;
export const capitalize = _capitalize.default;
export const flip = _flip.default;
export const negate = _negate.default;
export const partial = _partial.default;
export const includes = _includes.default;

/*
    Grid Utils
 */

export const arrayToGrid = (array, colCount) => {
    let rowCount = Math.ceil(array.length / colCount);
    let rows = [];
    let max = rowCount * colCount;
    for (let i=0; i<max; i += colCount) {
        rows.push(array.slice(i, i + colCount));
    }
    return rows;
};

/*
    Enums and shared prop types
 */

export const ItemExplorerViewOptions = {
    Grid: "Grid",
    List: "List"
};
export const ItemExplorerViewOptionsPropType = PropTypes.oneOf([ItemExplorerViewOptions.Grid, ItemExplorerViewOptions.List]);

export const SortDirection = {
    Ascending: "Ascending",
    Descending: "Descending",
    Unsorted: "Unsorted"
};
export const SortDirectionPropType = PropTypes.oneOf([SortDirection.Ascending, SortDirection.Descending, SortDirection.Unsorted]);

export const ResourcePropShape = (fields) => {
    let keys = Object.keys(fields);
    let shape = {};
    for (let key of keys) {
        shape[key] = PropTypes.shape({
            value: fields[key],
            field: PropTypes.string.isRequired,
            isSaving: PropTypes.bool.isRequired
        })
    }

    shape.id = PropTypes.shape({
        value: PropTypes.string.isRequired,
        field: PropTypes.string.isRequired,
        isSaving: PropTypes.bool.isRequired
    });

    return PropTypes.shape(shape);
};

/*
    Shared logic
 */

export const arrayOfSorts = sorts => Object.keys(sorts || {}).reduce((array, key) => {
    const sort = sorts[key];
    if (sort !== SortDirection.Unsorted) {
        array.push({
            field: key,
            direction: sort
        });
    }
    return array;
}, []);

export const toggleSort = (prevSorts, field) => Object.keys(prevSorts || {}).reduce((nextSorts, key) => {
    const sort = prevSorts[key];

    if (key !== field) {
        nextSorts[key] = SortDirection.Unsorted;
    } else if (sort === SortDirection.Unsorted) {
        nextSorts[key] = SortDirection.Descending;
    } else {
        nextSorts[key] = (sort === SortDirection.Descending)
            ? SortDirection.Ascending
            : SortDirection.Descending;
    }

    return nextSorts;
}, {});


export const isUnsorted = sorts => Object.keys(sorts).every(key => sorts[key] === SortDirection.Unsorted);

/**
* Returns the index of the given object with matching objectId in the
* array.
*/
export const getIndexOfObjectInArray = (array, objectId) => {
  return array.map(function(x) {return x.id; }).indexOf(objectId);
};


/*
    Helpers
 */

// creates a pathOverride function to move the give paths up one resource
// then making the field path reference the given field of the parent
export const toParentField = (field) => {
    return (fieldPath, resourcePath) => {
        let newField = fieldPath.slice(0, fieldPath.length - 1).concat(field);
        let newResource = fieldPath.slice(0, resourcePath.length - 1);
        return {
            fieldPath: newField,
            resourcePath: newResource
        }
    }
};

const spreadPath = (path) => {
  let fullPath = [];
  // Path strings may contain multiple parts.
  // e.g. "ageGate.min", "steps.0", "shareMap.FACEBOOK.title"
  // need to go through path and split these pairs to make following loop easier
  for (let part of path) {
    fullPath.push(...part.split('.'));
  }
  return fullPath
};

export const isBreakInHub = (brk) => !isNoU(brk.hubId);

/**
 * For value resolution of platform resources we take into account that a platform may have arrays in a different order
 * This method will resolve any array indices in the given path by looking up that index in the masterBrk
 *  and then finding the matching value by id in the platformBrk
 * If path is not provided or empty, the platformBrk is returned
 * If at any point in path, a null or undefined value is found it is returned
 * @param masterBrk Break used to resolve resources the path.
 * @param platformBrk Break to get values from.
 * @param path Array of strings determining the path to the desired value.
 * @return {*} Value at end of the given path.
 */
export const getBreakResource = (masterBrk, platformBrk, path) => {
  if (isNoU(platformBrk)
    || isNoU(path)
    || !Array.isArray(path)
    || path.length === 0) {
    return platformBrk;
  }

  let fullPath = spreadPath(path);

  let resource = platformBrk;
  let masterResource = masterBrk;
  for (let part of fullPath) {
    // if part can be cast to a number, its an index of an array
    let index = parseInt(part);
    if (isNaN(index)) {
      // not an index, get value normally
      resource = resource[part];

      // its possible for a platform to have a field that master doesn't
      // so null check
      if (!isNoU(masterResource)) {
        masterResource = masterResource[part];
      }
    } else {
      // if it is an index we need to resolve it through the master break
      // we assume, during this iteration, resource and masterResource are both arrays

      // its possible for a platform to have a field that master doesn't
      // so add null check
      if (!isNoU(masterResource)) {
        // temp so we can still get to the array and the master version of the resource we want
        let nextMasterResource = masterResource[part];
        // find the platform version of the resource
        try {
            resource = resource.find((item) =>  item.id === nextMasterResource.id);
            masterResource = nextMasterResource;
        } catch (e) {
            console.error(e.message)
        }
      } else {
        // if master doesn't have this array, we'll just use the platform's version
        resource = resource[part];
      }
    }

    // return if we run into a null or undefined value
    // because we can't continue
    if (isNoU(resource)) {
      return resource
    }
  }

  return resource;
};

export const setBreakResource = (masterBrk, platformBrk, path, value) => {
  let fullPath = Array.isArray(path) ? spreadPath(path) : path.split(".");

  // Assuming field is last element in path
  // Make resourcePath to extract the object that needs the field set
  let resourcePath = fullPath.slice(0, -1);
  let field = fullPath[fullPath.length - 1];

  let resource = getBreakResource(masterBrk, platformBrk, resourcePath);
  if (!isNoU(resource)) {
    resource[field] = value;
  }
};

export const extractMasterForBreak = (resourceMap, brk) => {
  if (!isNoU(brk.platform) && brk.platform !== "MASTER") {
    return resourceMap[brk.defaultParentId];
  }

  return brk;
};

export const extractResourceIdsWithPath = (masterResource, rootResource, resourcePath, includeRootResource = true) => {
    let rootResourceId = rootResource.resourceId;
    if (!rootResourceId) {
        console.warn("Root resource without resourceId", rootResource);
        return;
    }

    let parentIds = [];
    if (resourcePath.length > 0) {
        // Clone for safety
        let parentResourcePath = resourcePath.slice();
        while (parentResourcePath.length > 0) { // Edited resource only nested one layer deep, only need root resource
            let resource = getBreakResource(masterResource, rootResource, parentResourcePath);
            if (isNoU(resource) || Array.isArray(resource)) {
                // only include resourceIds of existing resources
                // also ignoring arrays, since arrays themselves aren't resources, but are included in resource path if they contain resources
                parentResourcePath = parentResourcePath.slice(0, -1);
                continue;
            }

            if (!resource.resourceId) {
                parentResourcePath = parentResourcePath.slice(0, -1);
                console.warn("Parent resource with no resourceId", resource, parentResourcePath);
                continue;
            }

            // Clone for safety
            parentIds.push(clone(resource.resourceId));

            // Slice off end again
            parentResourcePath = parentResourcePath.slice(0, -1);
        }
    }

    if (includeRootResource) {
        parentIds.push(clone(rootResourceId));
    }

    return parentIds;
};

export const addThousandsCommas = (x) => {
    return isNoU(x) ? "" : x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const interleave = (array1, array2) => {
  const interleaved = array1.reduce((result, value, idx) => {
    result.push(value, array2[idx]);
    return result;
  }, []);

  return interleaved;
};

// https://github.com/reduxjs/reselect/issues/159
export const uncurry = curriedFn => (...args) => args.reduce((left, right) => left(right), curriedFn);

// for cases when you want to partially apply the current state of the Redux store to a
// selector factory so that you don't have to pass down the entire state tree as props,
// which would result in unwanted re-renders.
//
// this works by first uncurrying the selector factory and then flipping its parameter
// list, and then partially applying the state to it.
//
// so if you pass a curried selector creator that has a type signature like this to bindSelector:
//   (...args) -> reduxState -> returnValue
//
// you get a selector like this back from bindSelector:
//   (...args) -> returnValue
export const bindSelector = (selector, state) => partial(flip(uncurry(selector)), state);

// https://github.com/facebook/fbjs/blob/master/packages/fbjs/src/key-mirror/keyMirror.js
export const keyMirror = obj => Object.keys(obj).reduce((result, key) => {
  if (obj.hasOwnProperty(key)) {
    result[key] = key;
  }
  return result;
}, {});

export const byField = field => (a, b) => a[field] - b[field];

export const alphabetically = (a, b) => {
  if (a > b) {
    return 1;
  } else if (b > a) {
    return -1;
  } else {
    return 0;
  }
};

// special comparator for string fields since `byField` above won't work for that
export const byStringField = field => (a, b) => alphabetically(a[field], b[field]);

export const makeBreakStepSectionKey = (id) => `breakStepsSection${id}`;

export const platformMappings = Object.freeze({
  DAILYBREAK: 'Web',
  DEMO: 'Demo',
  EMBED: 'Embed'
});

export const Platforms = Object.freeze(Object.keys(platformMappings).concat("MASTER"));

export const platformCircledLetterClasses = {
    DAILYBREAK: 'circled-letter-web',
    DEMO: 'circled-letter-demo',
    EMBED: 'circled-letter-embed'
};

export const SupportedLangs = [
    'en-US',
    'en-GB',
    'es-LA'
];

export const BreakSiteFiltersByLanguage = Object.freeze({
  'en-US': 'break_site_dailybreak',
  'es-LA': 'break_site_el_dailybreak',
  'en-GB': 'break_site_dailybreak_uk'
});

export const PrizeSiteFiltersByLanguage = Object.freeze({
  'en-US': 'prize_site_dailybreak',
  'es-LA': 'prize_site_el_dailybreak',
  'en-GB': 'prize_site_dailybreak_uk'
});

export const LanguagesByBreakSiteFilter = Object.freeze({
  'break_site_dailybreak': 'en-US',
  'break_site_el_dailybreak': 'es-LA',
  'break_site_dailybreak_uk': 'en-GB'
});

export const LanguagesByPrizeSiteFilter = Object.freeze({
  'prize_site_dailybreak': 'en-US',
  'prize_site_el_dailybreak': 'es-LA',
  'prize_site_dailybreak_uk': 'en-GB'
});

export const SiteNameKeys = Object.freeze({
  'en-US': 'filter.prize_site_dailybreak',
  'es-LA': 'filter.prize_site_el_dailybreak',
  'en-GB': 'filter.prize_site_dailybreak_uk'
});

export const SitesByLanguage = Object.freeze({
  'en-US': 'Dailybreak',
  'es-LA': 'El Dailybreak',
  'en-GB': 'Dailybreak UK'
});

export const CurrenciesByLanguage = Object.freeze({
  'en-US': 'USD',
  'es-LA': 'USD',
  'en-GB': 'GBP'
});

export const CurrencyPrefixesByLanguage = Object.freeze({
  'en-US': '$',
  'es-LA': '$',
  'en-GB': '£'
});

// dashboards: only one filter from each group may be active at a time.
export const exclusiveBreakFilterGroups = Object.freeze([
  ['break_type_sponsored', 'break_type_editorial'],
  ['publish_status_draft', 'publish_status_ready', 'publish_status_live', 'publish_status_archived'],
  ['break_site_dailybreak', 'break_site_bestdailyrecipes', 'break_site_fifaunilever'],
  ['created_today', 'created_last_7_days', 'created_last_30_days']
]);

export const exclusiveRestrictedBreakFilterGroups = Object.freeze([
  ['publish_status_draft', 'publish_status_ready', 'publish_status_live', 'publish_status_archived'],
  ['break_site_dailybreak', 'break_site_bestdailyreceipes'],
  ['created_today', 'created_last_7_days', 'created_last_30_days']
]);

export const stepFilters = Object.freeze([
  'step_article',
  'step_checklist',
  'step_complete',
  'step_custom_html',
  'step_custom_json',
  'step_drag_drop',
  'step_filtering',
  'step_gallery',  
  'step_grocery',  
  'step_leadgen',
  'step_listicle',
  'step_mediaupload',
  'step_memory_match',
  'step_personality_quiz',
  'step_pinterest',
  'step_poll',
  'step_puzzle',
  'step_receipt_campaign',
  'step_referral',
  'step_review',
  'step_spot_difference',
  'step_store_finder',
  'step_text_trivia',
  'step_ugc',
  'step_video'
]);

export const restrictedBreakFilterGroups = Object.freeze([
  ['my_resources'],
  ...exclusiveRestrictedBreakFilterGroups,
  stepFilters
]);

export const breakFilterGroups = Object.freeze([
  ['my_resources'],
  ...exclusiveBreakFilterGroups,
  stepFilters
]);

export const exclusivePrizeFilterGroups = Object.freeze([
  ['prize_site_dailybreak', 'prize_site_el_dailybreak', 'prize_site_dailybreak_uk'],
  ['prize_ended_today', 'prize_ended_last_7_days', 'prize_ended_last_30_days']
]);

export const prizeFilterGroups = Object.freeze([
  ...exclusivePrizeFilterGroups
]);

export const mapPropsToDropdownItems = props => {
  const groupedFilterOptions = (props.filterGroups || []).map((group, i) => {
    const possibleFiltersInGroup = group.filter(negate(props.hasNamedFilter));

    // Check to see if our groups are different sizes to prove we've selected something.
    const filtered = possibleFiltersInGroup.length !== group.length && (

      // Check if this isn't the step types group, but only if we're filtering on Challenge.
      props.resourceType === ResourceApi.Types.Challenge && group !== stepFilters
    );

    return possibleFiltersInGroup.map((filter, i) => {
      const message = props.intl.formatMessage({id: 'filter.' + filter});
      return (
        <Dropdown.Item
          key={filter}
          value={filter}
          text={message}
          disabled={filtered}
          content={<p className='filter-option' key={filter}>{message}</p>}
          onClick={() => props.onFilterSelected(filter)} />
      );
    });
  });

  const dividers = Array(groupedFilterOptions.length).fill(<Dropdown.Divider/>);

  // elide trailing divider
  return interleave(groupedFilterOptions, dividers).slice(0,-1);
};

export const PrizePublishStatus = Object.freeze(keyMirror({
  UNSCHEDULED: null,
  SCHEDULED: null,
  ACTIVE: null,
  UNAWARDED: null,
  PENDING_AWARDING: null,
  PENDING_CLAIM: null,
  CLAIMED: null,
  COMPLETE: null,
  UNKNOWN: null,
}));

export const PrizeType = Object.freeze(keyMirror({
  RAFFLE: null,
  CHALLENGE: null
}));

export const PrizeTypeMapping = Object.freeze({
  [PrizeType.RAFFLE]: 'editorial',
  [PrizeType.CHALLENGE]: 'sponsored'
});

export const canDeletePrize = prize => {
    // Can only delete prizes that have not started yet
    const startMoment = moment(prize.startTime);
  const isScheduled = startMoment.isAfter(moment());

  const hasNoEntries = prize.numberOfEntries === 0;
  return isScheduled && hasNoEntries;
};

export const WinnerStatus = Object.freeze(keyMirror({
  NOTIFIED: null,
  PREORDERED: null,
  ORDERED: null,
  FORFEITED: null
}));

export const UserGroups = Object.freeze({
  Root: 'root',
  Admin: 'admin',
  RestrictedAdmin: 'restricted admin',
  Intern: 'interns'
});

// for convenience because these groups have some overlapping restrictions.
export const RestrictedUserGroups = Object.freeze([
  UserGroups.Intern,
  UserGroups.RestrictedAdmin
]);

// convenience helper for checking if a user has a "READ" persmission in `permissions`
export const canRead = permissions => includes(permissions, 'READ');

export const canDownload = permissions => includes(permissions, 'DOWNLOAD');

export const breakUrl = (permalink, platform) => {
  var startURL, midURL, endURL;

  switch (platform) {
    case "MASTER": {
      startURL = "break/";
      midURL = permalink;
      endURL = "";
      break;
    }
    case "DAILYBREAK": {
      startURL = "break/";
      midURL = permalink;
      endURL = "";
      break;
    }
    case "EMBED": {
      startURL = "embed/";
      midURL = permalink;
      endURL = "EMBED";
      break;
    }
    case "DEMO": {
      startURL = "embed/";
      midURL = permalink;
      endURL = "DEMO";
      break;
    }
  }

return startURL+midURL+"/"+endURL;
};

// matches current router url pathname against keys in `dict`
export const findByPathname = (dict, pathname) => {
  const matchingPath = Object.keys(dict).find(path => matchPath(pathname, {
    path,
    exact: true
  }));

  return (matchingPath && matchingPath in dict)
    ? dict[matchingPath]
    : [];
};

// determines whether a set of resource permissions allows for user tagging CRUD operations.
export const hasAllUserTagPermissions = resourcePermissions => {
  const userTagPermissions = resourcePermissions.find(({name}) => name === ResourceApi.Types.ActionTagLink.toLowerCase());
  return userTagPermissions
    ? ['CREATE', 'READ', 'EDIT', 'DELETE'].every(action => includes(userTagPermissions.resourceActions, action))
    : false;
};

export const undeletedItems = (ary) => ary.filter(item => !item.deleted);

export const states = [
  {name: 'Alabama', abbrev: 'AL'},
  {name: 'Alaska', abbrev: 'AK'},
  {name: 'Arizona', abbrev: 'AZ'},
  {name: 'Arkansas', abbrev: 'AR'},
  {name: 'California', abbrev: 'CA'},
  {name: 'Colorado', abbrev: 'CO'},
  {name: 'Connecticut', abbrev: 'CT'},
  {name: 'Delaware', abbrev: 'DE'},
  {name: 'District of Columbia', abbrev: 'DC'},
  {name: 'Florida', abbrev: 'FL'},
  {name: 'Georgia', abbrev: 'GA'},
  {name: 'Hawaii', abbrev: 'HI'},
  {name: 'Idaho', abbrev: 'ID'},
  {name: 'Illinois', abbrev: 'IL'},
  {name: 'Indiana', abbrev: 'IN'},
  {name: 'Iowa', abbrev: 'IA'},
  {name: 'Kansas', abbrev: 'KS'},
  {name: 'Kentucky', abbrev: 'KY'},
  {name: 'Louisiana', abbrev: 'LA'},
  {name: 'Maine', abbrev: 'ME'},
  {name: 'Maryland', abbrev: 'MD'},
  {name: 'Massachusetts', abbrev: 'MA'},
  {name: 'Michigan', abbrev: 'MI'},
  {name: 'Minnesota', abbrev: 'MN'},
  {name: 'Mississippi', abbrev: 'MS'},
  {name: 'Missouri', abbrev: 'MO'},
  {name: 'Montana', abbrev: 'MT'},
  {name: 'Nebraska', abbrev: 'NE'},
  {name: 'Nevada', abbrev: 'NV'},
  {name: 'New Hampshire', abbrev: 'NH'},
  {name: 'New Jersey', abbrev: 'NJ'},
  {name: 'New Mexico', abbrev: 'NM'},
  {name: 'New York', abbrev: 'NY'},
  {name: 'North Carolina', abbrev: 'NC'},
  {name: 'North Dakota', abbrev: 'ND'},
  {name: 'Ohio', abbrev: 'OH'},
  {name: 'Oklahoma', abbrev: 'OK'},
  {name: 'Oregon', abbrev: 'OR'},
  {name: 'Pennsylvania', abbrev: 'PA'},
  {name: 'Rhode Island', abbrev: 'RI'},
  {name: 'South Carolina', abbrev: 'SC'},
  {name: 'South Dakota', abbrev: 'SD'},
  {name: 'Tennessee', abbrev: 'TN'},
  {name: 'Texas', abbrev: 'TX'},
  {name: 'Utah', abbrev: 'UT'},
  {name: 'Vermont', abbrev: 'VT'},
  {name: 'Virginia', abbrev: 'VA'},
  {name: 'Washington', abbrev: 'WA'},
  {name: 'West Virginia', abbrev: 'WV'},
  {name: 'Wisconsin', abbrev: 'WI'},
  {name: 'Wyoming', abbrev: 'WY'}
];



// WEBPACK FOOTER //
// ./public/utils/DBUtils.js